import { Player, SelectionData, TeamsResponse, Team, SkillDistribution, TrainingDrill, SessionSetup, SessionPlan, TrainingSession, TrainingSessionResponse, SaveTrainingSessionRequest, UpdateTrainingSessionRequest, YouTubeVideoInfo, CsvUploadResult, YouTubeSearchRequest, YouTubeSearchResponse, BulkCreateDrillsRequest, BulkCreateDrillsResponse, FindSimilarDrillsRequest, FindSimilarDrillsResponse, ReferenceData, ShareSelectionRequest, ShareSelectionResponse, SquadAnalysisResult, CreateSelectionRequest, SelectionSummary, ReseedDrillsRequest, ReseedDrillsResponse, BuildFromPdfResponse, SessionNoteResponse, AddSessionNoteRequest, CommentResponse, CommentReplyResponse, AddCommentRequest, ReactionSummaryResponse, ToggleReactionRequest, ToggleFavouriteResponse, DrillResponse, DrillImageMetadata, DrillVideoMetadata, DrillAnimation, AnimationRequest, Sport, CreatePlayerWallPostRequest, PlayerWallPostResponse, InviteInfoResponse, AcceptInviteRequest, AcceptInviteResponse, AdminUser, UpdateAdminStatusRequest } from './Model';
import { Result } from './Result';
import { HttpHelpers } from './HttpHelpers';

export class RestClient {
  protected baseUrl: string;
  private httpHelpers: HttpHelpers;

  constructor(baseUrl?: string) {
    // Initialize HTTP helpers
    this.httpHelpers = new HttpHelpers();
    
    // Auto-detect environment: development vs production
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else if (process.env.NODE_ENV === 'development') {
      // Development: React dev server (3000) -> .NET server (5052)
      this.baseUrl = 'http://localhost:5052/api';
    } else {
      // Production: Same origin (integrated server)
      this.baseUrl = '/api';
    }
    console.log(`RestClient initialized with baseUrl: ${this.baseUrl}`);
  }

  // Authentication endpoints
  async login(username: string, password: string): Promise<Result<any>> {
    const url = `${this.baseUrl}/auth/login`;
    return this.httpHelpers.fetchWithBody(url, 'POST', { username, password }, 'Login');
  }

  async signup(username: string, password: string, nickname: string, teamName: string, sport: string): Promise<Result<any>> {
    const url = `${this.baseUrl}/auth/signup`;
    return this.httpHelpers.fetchWithBody(url, 'POST', { username, password, nickname, teamName, sport }, 'Sign up');
  }

  async forgotPassword(email: string): Promise<Result<{ message: string }>> {
    const url = `${this.baseUrl}/auth/forgot-password`;
    return this.httpHelpers.fetchWithBody(url, 'POST', { email }, 'Request password reset');
  }

  async resetPassword(token: string, newPassword: string): Promise<Result<{ message: string }>> {
    const url = `${this.baseUrl}/auth/reset-password`;
    return this.httpHelpers.fetchWithBody(url, 'POST', { token, newPassword }, 'Reset password');
  }

  // Selection endpoint
  async getSelectionData(selectionId: number): Promise<Result<SelectionData>> {
    const url = `${this.baseUrl}/selection/${selectionId}`;
    return this.httpHelpers.fetchWithoutBody<SelectionData>(url, 'GET', 'Loading selection data');
  }

  async shareSelection(selectionId: number, request: ShareSelectionRequest): Promise<Result<ShareSelectionResponse>> {
    const url = `${this.baseUrl}/selection/${selectionId}/share`;
    return this.httpHelpers.fetchWithBody<ShareSelectionRequest, ShareSelectionResponse>(url, 'POST', request, 'Sharing selection');
  }

  async createSelection(request: CreateSelectionRequest): Promise<Result<SelectionSummary>> {
    const url = `${this.baseUrl}/selection`;
    return this.httpHelpers.fetchWithBody<CreateSelectionRequest, SelectionSummary>(url, 'POST', request, 'Creating selection');
  }

  // Reference data endpoint
  async getReferenceData(): Promise<Result<ReferenceData>> {
    const url = `${this.baseUrl}/referencedata`;
    return this.httpHelpers.fetchWithoutBody<ReferenceData>(url, 'GET', 'Loading reference data');
  }

  async updateTeam(teamId: number, color: string, colorName: string,
    version: number, selectionDataId: number
  ): Promise<Result<any>> {
    const url = `${this.baseUrl}/team/${teamId}`;
    return this.httpHelpers.fetchWithBody(url, 'PUT', { 
      color, 
      colorName,
      version,
      selectionDataId
    }, 'Updating team');
  }

  // Player endpoints
  async addPlayer(player: Player, selectionDataId: number): Promise<Result<Player>> {
    const url = `${this.baseUrl}/players`;
    const playerWithSelectionId = { ...player, selectionDataId };
    return this.httpHelpers.fetchWithBody<Player, Player>(url, 'POST', playerWithSelectionId, 'Adding player');
  }

  async updatePlayer(player: Player): Promise<Result<Player>> {
    const url = `${this.baseUrl}/players/${player.id}`;
    return this.httpHelpers.fetchWithBody<Player, Player>(url, 'PUT', player, 'Updating player');
  }

  async downloadCsvTemplate(selectionDataId: number): Promise<Result<Blob>> {
    const url = `${this.baseUrl}/players/csv-template/${selectionDataId}`;
    return this.httpHelpers.fetchBlobGet(url, 'Downloading CSV template');
  }

  async uploadPlayersCsv(file: File, selectionDataId: number): Promise<Result<CsvUploadResult>> {
    const url = `${this.baseUrl}/players/upload-csv`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('selectionDataId', selectionDataId.toString());
    return this.httpHelpers.fetchFormData<CsvUploadResult>(url, formData, 'Uploading players CSV');
  }

  // Team assignment endpoints
  async assignTeams(players: Player[], maxPlayers: number, numberOfTeams: number = 2, teams?: Team[], selectionDataId?: number, skillDistribution?: SkillDistribution): Promise<Result<TeamsResponse>> {
    const url = `${this.baseUrl}/team-assignment`;
    return this.httpHelpers.fetchWithBody<{ players: Player[], maxPlayers: number, teams: number, teamsInfo?: Team[], selectionDataId?: number, skillDistribution?: SkillDistribution }, TeamsResponse>(
      url, 'POST', { players, maxPlayers, teams: numberOfTeams, teamsInfo: teams, selectionDataId, skillDistribution }, 'Assigning teams'
    );
  }

  async assignTeamsFromText(message: string, maxPlayers: number, numberOfTeams: number = 2, teams?: Team[], selectionDataId?: number, skillDistribution?: SkillDistribution): Promise<Result<TeamsResponse>> {
    const url = `${this.baseUrl}/team-assignment/from-text`;
    return this.httpHelpers.fetchWithBody<{ message: string, maxPlayers: number, teams: number, teamsInfo?: Team[], selectionDataId?: number, skillDistribution?: SkillDistribution }, TeamsResponse>(
      url, 'POST', { message, maxPlayers, teams: numberOfTeams, teamsInfo: teams, selectionDataId, skillDistribution }, 'Assigning teams from text'
    );
  }

  async captureTeams(teams: Team[]): Promise<Result<Blob>> {
    const url = `${this.baseUrl}/team-capture`;
    const requestData = { teams };
    return this.httpHelpers.fetchBlob(url, 'POST', requestData, 'Capturing teams');
  }

  // Training drills endpoints
  async getDrills(sport?: Sport, includeHidden?: boolean): Promise<Result<DrillResponse[]>> {
    const params = new URLSearchParams();
    if (sport) params.set('sport', sport);
    if (includeHidden) params.set('includeHidden', 'true');
    const query = params.toString();
    const url = query
      ? `${this.baseUrl}/drills?${query}`
      : `${this.baseUrl}/drills`;
    return this.httpHelpers.fetchWithoutBody<DrillResponse[]>(url, 'GET', 'Loading training drills');
  }

  async getDrill(id: number): Promise<Result<TrainingDrill>> {
    const url = `${this.baseUrl}/drills/${id}`;
    return this.httpHelpers.fetchWithoutBody<TrainingDrill>(url, 'GET', 'Loading drill');
  }

  async updateDrill(id: number, drill: TrainingDrill): Promise<Result<TrainingDrill>> {
    const url = `${this.baseUrl}/drills/${id}`;
    return this.httpHelpers.fetchWithBody<TrainingDrill, TrainingDrill>(url, 'PUT', drill, 'Updating drill');
  }

  async createDrill(drill: TrainingDrill): Promise<Result<TrainingDrill>> {
    const url = `${this.baseUrl}/drills`;
    return this.httpHelpers.fetchWithBody<TrainingDrill, TrainingDrill>(url, 'POST', drill, 'Creating drill');
  }

  async fetchYouTubeDetails(youtubeUrl: string, sport?: Sport): Promise<Result<YouTubeVideoInfo>> {
    const url = `${this.baseUrl}/drills/fetch-youtube`;
    return this.httpHelpers.fetchWithBody<{ youtubeUrl: string; sport?: string }, YouTubeVideoInfo>(url, 'POST', { youtubeUrl, sport }, 'Fetching YouTube details');
  }

  async searchYouTube(request: YouTubeSearchRequest): Promise<Result<YouTubeSearchResponse>> {
    const url = `${this.baseUrl}/drills/search-youtube`;
    return this.httpHelpers.fetchWithBody<YouTubeSearchRequest, YouTubeSearchResponse>(url, 'POST', request, 'Searching YouTube');
  }

  async bulkCreateDrills(request: BulkCreateDrillsRequest): Promise<Result<BulkCreateDrillsResponse>> {
    const url = `${this.baseUrl}/drills/bulk-create`;
    return this.httpHelpers.fetchWithBody<BulkCreateDrillsRequest, BulkCreateDrillsResponse>(url, 'POST', request, 'Creating drills');
  }

  async findSimilarDrills(request: FindSimilarDrillsRequest): Promise<Result<FindSimilarDrillsResponse>> {
    const url = `${this.baseUrl}/drills/similar`;
    return this.httpHelpers.fetchWithBody<FindSimilarDrillsRequest, FindSimilarDrillsResponse>(url, 'POST', request, 'Finding similar drills');
  }

  // Drill animation endpoints
  async generateDrillAnimation(drillId: number): Promise<Result<DrillAnimation>> {
    const url = `${this.baseUrl}/drills/${drillId}/animation`;
    return this.httpHelpers.fetchWithBody<{}, DrillAnimation>(url, 'POST', {}, 'Generating drill animation');
  }

  async generateAnimationFromDescription(request: AnimationRequest): Promise<Result<DrillAnimation>> {
    const url = `${this.baseUrl}/drills/animation/generate`;
    return this.httpHelpers.fetchWithBody<AnimationRequest, DrillAnimation>(url, 'POST', request, 'Generating animation from description');
  }

  async clearDrillAnimation(drillId: number): Promise<Result<void>> {
    const url = `${this.baseUrl}/drills/${drillId}/animation`;
    return this.httpHelpers.fetchDelete(url, 'Clearing drill animation');
  }

  // Drill favourites endpoints
  async toggleDrillFavourite(drillId: number): Promise<Result<ToggleFavouriteResponse>> {
    const url = `${this.baseUrl}/drills/${drillId}/favourite`;
    return this.httpHelpers.fetchWithBody<{}, ToggleFavouriteResponse>(url, 'PUT', {}, 'Toggling drill favourite');
  }

  // Session planning endpoints
  async generateSessionPlan(setup: SessionSetup): Promise<Result<SessionPlan>> {
    const url = `${this.baseUrl}/sessionplan/generate`;
    return this.httpHelpers.fetchWithBody<SessionSetup, SessionPlan>(url, 'POST', setup, 'Generating session plan');
  }

  async saveTrainingSession(request: SaveTrainingSessionRequest): Promise<Result<TrainingSession>> {
    const url = `${this.baseUrl}/sessionplan`;
    return this.httpHelpers.fetchWithBody<SaveTrainingSessionRequest, TrainingSession>(url, 'POST', request, 'Saving training session');
  }

  async updateTrainingSession(request: UpdateTrainingSessionRequest): Promise<Result<TrainingSession>> {
    const url = `${this.baseUrl}/sessionplan/${request.id}`;
    return this.httpHelpers.fetchWithBody<UpdateTrainingSessionRequest, TrainingSession>(url, 'PUT', request, 'Updating training session');
  }

  async getTrainingSessions(sport?: Sport): Promise<Result<TrainingSessionResponse[]>> {
    const url = sport
      ? `${this.baseUrl}/sessionplan?sport=${encodeURIComponent(sport)}`
      : `${this.baseUrl}/sessionplan`;
    return this.httpHelpers.fetchWithoutBody<TrainingSessionResponse[]>(url, 'GET', 'Loading training sessions');
  }

  async getTrainingSession(id: number): Promise<Result<TrainingSessionResponse>> {
    const url = `${this.baseUrl}/sessionplan/${id}`;
    return this.httpHelpers.fetchWithoutBody<TrainingSessionResponse>(url, 'GET', 'Loading training session');
  }

  async deleteTrainingSession(sessionId: number): Promise<Result<void>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}`;
    return this.httpHelpers.fetchDelete(url, 'Deleting training session');
  }

  // Squad analysis endpoints
  async getSquadAnalysis(selectionId: number): Promise<Result<SquadAnalysisResult>> {
    const url = `${this.baseUrl}/squadanalysis/${selectionId}`;
    return this.httpHelpers.fetchWithoutBody<SquadAnalysisResult>(url, 'GET', 'Loading squad analysis');
  }

  // Reseed drills endpoint
  async reseedDrills(request: ReseedDrillsRequest): Promise<Result<ReseedDrillsResponse>> {
    const url = `${this.baseUrl}/drills/reseed`;
    return this.httpHelpers.fetchWithBody<ReseedDrillsRequest, ReseedDrillsResponse>(url, 'POST', request, 'Reseeding drills database');
  }

  // Session notes endpoints
  async getSessionNotes(sessionId: number): Promise<Result<SessionNoteResponse[]>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/notes`;
    return this.httpHelpers.fetchWithoutBody<SessionNoteResponse[]>(url, 'GET', 'Loading session notes');
  }

  async addSessionNote(sessionId: number, request: AddSessionNoteRequest): Promise<Result<SessionNoteResponse>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/notes`;
    return this.httpHelpers.fetchWithBody<AddSessionNoteRequest, SessionNoteResponse>(url, 'POST', request, 'Adding session note');
  }

  async deleteSessionNote(sessionId: number, noteId: number): Promise<Result<void>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/notes/${noteId}`;
    return this.httpHelpers.fetchDelete(url, 'Deleting session note');
  }

  // Comment endpoints
  async getComments(sessionId: number): Promise<Result<CommentResponse[]>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/comments`;
    return this.httpHelpers.fetchWithoutBody<CommentResponse[]>(url, 'GET', 'Loading comments');
  }

  async addComment(sessionId: number, request: AddCommentRequest): Promise<Result<CommentResponse>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/comments`;
    return this.httpHelpers.fetchWithBody<AddCommentRequest, CommentResponse>(url, 'POST', request, 'Adding comment');
  }

  async addReply(sessionId: number, commentId: number, request: AddCommentRequest): Promise<Result<CommentReplyResponse>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/comments/${commentId}/replies`;
    return this.httpHelpers.fetchWithBody<AddCommentRequest, CommentReplyResponse>(url, 'POST', request, 'Adding reply');
  }

  async deleteComment(sessionId: number, commentId: number): Promise<Result<void>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/comments/${commentId}`;
    return this.httpHelpers.fetchDelete(url, 'Deleting comment');
  }

  // Reaction endpoints
  async getSessionReaction(sessionId: number): Promise<Result<ReactionSummaryResponse>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/reactions`;
    return this.httpHelpers.fetchWithoutBody<ReactionSummaryResponse>(url, 'GET', 'Loading reactions');
  }

  async toggleSessionReaction(sessionId: number, request: ToggleReactionRequest): Promise<Result<ReactionSummaryResponse>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/reactions`;
    return this.httpHelpers.fetchWithBody<ToggleReactionRequest, ReactionSummaryResponse>(url, 'PUT', request, 'Updating reaction');
  }

  async toggleCommentReaction(sessionId: number, commentId: number, request: ToggleReactionRequest): Promise<Result<ReactionSummaryResponse>> {
    const url = `${this.baseUrl}/sessionplan/${sessionId}/comments/${commentId}/reactions`;
    return this.httpHelpers.fetchWithBody<ToggleReactionRequest, ReactionSummaryResponse>(url, 'PUT', request, 'Updating reaction');
  }

  // Player wall endpoints
  async createPlayerWallPost(request: CreatePlayerWallPostRequest): Promise<Result<PlayerWallPostResponse[]>> {
    const url = `${this.baseUrl}/playerwall`;
    return this.httpHelpers.fetchWithBody<CreatePlayerWallPostRequest, PlayerWallPostResponse[]>(url, 'POST', request, 'Sharing drill to player wall');
  }

  async getPlayerWallPosts(selectionId: number, playerId: number): Promise<Result<PlayerWallPostResponse[]>> {
    const url = `${this.baseUrl}/playerwall/${selectionId}/${playerId}`;
    return this.httpHelpers.fetchWithoutBody<PlayerWallPostResponse[]>(url, 'GET', 'Loading player wall');
  }

  // Invite endpoints (no auth required)
  async getInviteInfo(token: string): Promise<Result<InviteInfoResponse>> {
    const url = `${this.baseUrl}/auth/invite-info?token=${encodeURIComponent(token)}`;
    return this.httpHelpers.fetchWithoutBody<InviteInfoResponse>(url, 'GET', 'Loading invitation details');
  }

  async acceptInvite(request: AcceptInviteRequest): Promise<Result<AcceptInviteResponse>> {
    const url = `${this.baseUrl}/auth/accept-invite`;
    return this.httpHelpers.fetchWithBody<AcceptInviteRequest, AcceptInviteResponse>(url, 'POST', request, 'Accepting invitation');
  }

  // Admin endpoints
  async getAdminUsers(): Promise<Result<AdminUser[]>> {
    const url = `${this.baseUrl}/admin/users`;
    return this.httpHelpers.fetchWithoutBody<AdminUser[]>(url, 'GET', 'Loading users');
  }

  async updateUserAdminStatus(userId: number, request: UpdateAdminStatusRequest): Promise<Result<AdminUser>> {
    const url = `${this.baseUrl}/admin/users/${userId}/admin`;
    return this.httpHelpers.fetchWithBody<UpdateAdminStatusRequest, AdminUser>(url, 'PUT', request, 'Updating admin status');
  }

  // Drill image endpoints
  async uploadDrillImages(drillId: number, files: File[]): Promise<Result<DrillImageMetadata[]>> {
    const url = `${this.baseUrl}/drills/${drillId}/images`;
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return this.httpHelpers.fetchFormData<DrillImageMetadata[]>(url, formData, 'Uploading drill images');
  }

  async getDrillImageMetadata(drillId: number): Promise<Result<DrillImageMetadata[]>> {
    const url = `${this.baseUrl}/drills/${drillId}/images`;
    return this.httpHelpers.fetchWithoutBody<DrillImageMetadata[]>(url, 'GET', 'Loading drill images');
  }

  async deleteDrillImage(drillId: number, imageId: number): Promise<Result<void>> {
    const url = `${this.baseUrl}/drills/${drillId}/images/${imageId}`;
    return this.httpHelpers.fetchDelete(url, 'Deleting drill image');
  }

  getDrillImageUrl(drillId: number, imageId: number): string {
    return `${this.baseUrl}/drills/${drillId}/images/${imageId}`;
  }

  // Drill video endpoints
  async uploadDrillVideo(drillId: number, file: File): Promise<Result<DrillVideoMetadata>> {
    const url = `${this.baseUrl}/drills/${drillId}/videos`;
    const formData = new FormData();
    formData.append('file', file);
    return this.httpHelpers.fetchFormData<DrillVideoMetadata>(url, formData, 'Uploading drill video');
  }

  async getDrillVideoMetadata(drillId: number): Promise<Result<DrillVideoMetadata | null>> {
    const url = `${this.baseUrl}/drills/${drillId}/videos`;
    return this.httpHelpers.fetchWithoutBody<DrillVideoMetadata | null>(url, 'GET', 'Loading drill video');
  }

  async deleteDrillVideo(drillId: number, videoId: number): Promise<Result<void>> {
    const url = `${this.baseUrl}/drills/${drillId}/videos/${videoId}`;
    return this.httpHelpers.fetchDelete(url, 'Deleting drill video');
  }

  getDrillVideoUrl(drillId: number, videoId: number): string {
    return `${this.baseUrl}/drills/${drillId}/videos/${videoId}`;
  }

  // Build session from PDF endpoint
  async buildSessionFromPdf(file: File): Promise<Result<BuildFromPdfResponse>> {
    const url = `${this.baseUrl}/sessionplan/build-from-pdf`;
    const formData = new FormData();
    formData.append('file', file);
    return this.httpHelpers.fetchFormData<BuildFromPdfResponse>(url, formData, 'Building session from PDF');
  }
}

// Create a singleton instance
export const restClient = new RestClient();